'use client'

import { type ForwardedRef, useImperativeHandle, useRef } from 'react'
import ReactDatePicker, {
  type DatePickerProps as ReactDatePickerProps,
} from 'react-datepicker'

import { forwardRef } from '../../utils/index.ts'
import {
  type UseFormFieldControlProps,
  useFormFieldControl,
} from '../form-field/index.ts'
import { Icon } from '../icon/index.ts'
import { useInputStyles } from './use-input-styles.ts'
import { DateInput } from './date-input.tsx'

type DatePickerProps = Readonly<
  { showTime?: boolean } & UseFormFieldControlProps<HTMLInputElement> &
    ReactDatePickerProps
>

const DatePicker = forwardRef<
  HTMLInputElement & {
    focus(): void
  },
  DatePickerProps
>(
  (
    {
      selected,
      disabled,
      readOnly,
      id,
      hasError: hasErrorProp,
      showTime,
      dataTestId,
      dataTrackingId,
      ...datePickerProps
    },
    ref
  ) => {
    const { hasError, ...fieldProps } = useFormFieldControl<HTMLInputElement>({
      disabled,
      readOnly,
      id,
      hasError: hasErrorProp,
      dataTestId,
      dataTrackingId,
    })

    const datePickerRef = useRef<ReactDatePicker>(null)

    // In order for the focus logic to work on validation with react-hook-form, this ref needs to be extended with special focus logic.
    // The form crashes and gets stuck in a frozen state otherwise. The solution was discovered here -> https://github.com/react-hook-form/react-hook-form/issues/9126
    useImperativeHandle(
      ref as ForwardedRef<{
        focus(): void
      }>,
      () => {
        return {
          focus() {
            datePickerRef?.current?.setFocus()
          },
        }
      },
      []
    )

    const inputStyles = useInputStyles({ hasError })

    // todo: QF-998 - fix ref if we decide to keep this library
    return (
      <div className="relative">
        <div className="*:w-full">
          <ReactDatePicker
            className={inputStyles}
            selected={selected}
            {...datePickerProps}
            {...fieldProps}
            ref={datePickerRef}
            customInput={
              <DateInput
                date={selected?.toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                })}
                time={
                  showTime
                    ? selected?.toLocaleTimeString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : undefined
                }
                hasError={hasError}
                disabled={disabled}
                dataTestId={dataTestId}
                dataTrackingId={dataTrackingId}
              />
            }
          />
        </div>
        <span className="pointer-events-none absolute right-4 top-4">
          <Icon fill="solid" name="CalendarIcon" size="sm" color="info" />
        </span>
      </div>
    )
  }
)

export { DatePicker, type DatePickerProps }
